import { BadgeTone, DonutSegment, LegendEntry, PdfDynamicsCard, PdfSummaryCard, PdfTotalCard } from '../types/registryPdf';


// Лист A4 в CSS-пикселях (96 dpi)
export const PDF_PAGE_WIDTH = 794;
export const PDF_PAGE_HEIGHT = 1123;
export const PAGE_PADDING_X = 40;
export const PAGE_PADDING_TOP = 30;
export const PAGE_FOOTER_HEIGHT = 44;
export const CONTENT_WIDTH = PDF_PAGE_WIDTH - PAGE_PADDING_X * 2;
export const CONTENT_BOTTOM = PDF_PAGE_HEIGHT - PAGE_FOOTER_HEIGHT;
export const CONTENT_HEIGHT = CONTENT_BOTTOM - PAGE_PADDING_TOP;

// PT Sans (лицензия OFL) лежит в public/fonts, им пишется и сводка, и текст в PDF
export const REPORT_FONT_FAMILY = 'PT Sans Report';
export const REPORT_FONT_REGULAR = 'PT_Sans-Web-Regular.ttf';
export const REPORT_FONT_BOLD = 'PT_Sans-Web-Bold.ttf';

const SERIES_COLORS = ['#2160b0', '#f28c28', '#2a9d8f', '#8e5bd0', '#e6c229', '#e0457b', '#5aa9e6', '#8c6a4f', '#4caf50'];
const OTHERS_COLOR = '#c3c9d1';

// Если в легенде больше 9 участников, показываем топ-6, остальных одной строкой
const LEGEND_MAX_ENTRIES = 9;
const LEGEND_TOP_ENTRIES = 6;


// В данных встречаются и "Прекращен", и "Прекращён"
export const normalize = (text: string | null | undefined): string => {
    return (text || '').toLowerCase().replace(/ё/g, 'е').trim();
};


export const isMissing = (text: string | null | undefined): boolean => {
    return !text || normalize(text) === 'отсутствует';
};


export const capitalize = (text: string): string => {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
};


export const getStatusBadgeTone = (status: string | null | undefined): BadgeTone => {
    const normalizedStatus = normalize(status);
    if (['действует', 'действующий', 'возобновлен'].includes(normalizedStatus)) return 'positive';
    if (['прекращен', 'прекращенный', 'отменен'].includes(normalizedStatus)) return 'negative';
    if (['приостановлен', 'приостановленный'].includes(normalizedStatus)) return 'warning';
    return 'neutral';
};

export const formatFilterDescription = (conditions: { label: string; values: string[]; quoted?: boolean }[]): string => {
    const appliedConditions: string[] = [];
    conditions.forEach(condition => {
        if (condition.values.length === 0) return;
        const values = condition.quoted === false ? condition.values : condition.values.map(value => `«${value}»`);
        appliedConditions.push(`${condition.label} ${values.join(', ')}`);
    });
    return appliedConditions.length > 0 ? appliedConditions.join('; ') : 'не задан';
};


export interface SummaryCategory<T> {
    label: string;
    color: string;
    matches: (record: T) => boolean;
}


export const getSortedYears = <T>(records: T[], getYear: (record: T) => string): string[] => {
    const years: string[] = [];
    records.forEach(record => {
        const year = getYear(record);
        if (year && !years.includes(year)) years.push(year);
    });
    return years.sort();
};


export const buildTotalCard = <T>(
    title: string,
    records: T[],
    categories: SummaryCategory<T>[],
    getYear: (record: T) => string,
    showDynamics: boolean,
): PdfTotalCard => {
    const getSegments = (categoryRecords: T[]): DonutSegment[] => {
        return categories.map(category => ({
            label: category.label,
            color: category.color,
            value: categoryRecords.filter(category.matches).length,
        }));
    };

    const years = getSortedYears(records, getYear);
    const segmentsByYear: { [year: string]: DonutSegment[] } = {};
    if (showDynamics) {
        years.forEach(year => {
            segmentsByYear[year] = getSegments(records.filter(record => getYear(record) === year));
        });
    }

    return { kind: 'total', title, segments: getSegments(records), years, segmentsByYear: showDynamics ? segmentsByYear : null };
};


// Карточка "динамика": сколько записей у каждого участника (органа, лаборатории, заявителя, изготовителя) по годам
export const buildDynamicsCard = <T>(
    title: string,
    othersLabel: string,
    records: T[],
    getYear: (record: T) => string,
    getParticipants: (record: T) => { key: string; name: string }[],
): PdfDynamicsCard => {
    const years = getSortedYears(records, getYear);
    const participantNames: { [key: string]: string } = {};
    const recordsCount: { [key: string]: number } = {};
    const recordsCountByYear: { [year: string]: { [key: string]: number } } = {};

    records.forEach(record => {
        const year = getYear(record);
        if (!year) return;
        if (!recordsCountByYear[year]) recordsCountByYear[year] = {};
        getParticipants(record).forEach(participant => {
            if (!participantNames[participant.key]) participantNames[participant.key] = participant.name;
            recordsCount[participant.key] = (recordsCount[participant.key] || 0) + 1;
            recordsCountByYear[year][participant.key] = (recordsCountByYear[year][participant.key] || 0) + 1;
        });
    });

    const participantKeys = Object.keys(recordsCount).sort((first, second) => {
        if (recordsCount[first] !== recordsCount[second]) return recordsCount[second] - recordsCount[first];
        return participantNames[first].localeCompare(participantNames[second]);
    });
    const shownKeys = participantKeys.length > LEGEND_MAX_ENTRIES ? participantKeys.slice(0, LEGEND_TOP_ENTRIES) : participantKeys;
    const otherKeys = participantKeys.slice(shownKeys.length);

    const participantColors: { [key: string]: string } = {};
    shownKeys.forEach((key, index) => {
        participantColors[key] = SERIES_COLORS[index % SERIES_COLORS.length];
    });

    const legend: LegendEntry[] = shownKeys.map(key => ({
        label: participantNames[key],
        value: recordsCount[key],
        color: participantColors[key],
    }));
    if (otherKeys.length > 0) {
        const othersCount = otherKeys.reduce((sum, key) => sum + recordsCount[key], 0);
        legend.push({ label: `${othersLabel} (${otherKeys.length})`, value: othersCount, color: OTHERS_COLOR });
    }

    const segmentsByYear: { [year: string]: DonutSegment[] } = {};
    years.forEach(year => {
        const yearCount = recordsCountByYear[year] || {};
        const segments: DonutSegment[] = shownKeys.map(key => ({
            label: key,
            value: yearCount[key] || 0,
            color: participantColors[key],
        }));
        if (otherKeys.length > 0) {
            const othersCount = otherKeys.reduce((sum, key) => sum + (yearCount[key] || 0), 0);
            segments.push({ label: othersLabel, value: othersCount, color: OTHERS_COLOR });
        }
        segmentsByYear[year] = segments;
    });

    return { kind: 'dynamics', title, years, segmentsByYear, legend };
};


// Итоги и динамика встают в сводке по две в ряд, остальные карточки — на всю ширину
export const isFullWidthCard = (card: PdfSummaryCard): boolean => {
    return card.kind === 'table' || card.kind === 'tiles' || card.kind === 'image';
};
